<template>
  <b-card-code>
    <div class="bim-header mb-2">
      <div>
        <h4 class="mb-25">
          IFC betöltő (újraépített)
        </h4>
        <p class="mb-0 text-muted">
          Fal, gerenda, pillér, lépcső import + hossz ellenőrzés.
        </p>
      </div>
      <div class="d-flex align-items-end gap-1">
        <div class="min-w-260">
          <label class="mb-50">Projekt</label>
          <select
            v-model="selectedProjectId"
            class="form-control"
            :disabled="loading || uploading"
            @change="onProjectChanged"
          >
            <option value="">
              -- Válassz projektet --
            </option>
            <option
              v-for="project in projects"
              :key="project.id"
              :value="String(project.id)"
            >
              {{ project.work_number }} | {{ project.name }}
            </option>
          </select>
        </div>
        <input
          ref="ifcInput"
          type="file"
          accept=".ifc"
          class="d-none"
          @change="onIfcSelected"
        >
        <button
          class="btn btn-outline-info"
          :disabled="loading"
          @click="openImported3dView"
        >
          3D nézet
        </button>
        <button
          class="btn btn-primary"
          :disabled="uploading || !selectedProjectId"
          @click="$refs.ifcInput.click()"
        >
          {{ uploading ? 'Feldolgozás...' : 'IFC feltöltés' }}
        </button>
      </div>
    </div>

    <div class="row mb-2">
      <div class="col-md-7">
        <div class="table-responsive">
          <table class="table table-sm table-hover">
            <thead>
              <tr>
                <th>ID</th>
                <th>Fájl</th>
                <th>Státusz</th>
                <th>Elem</th>
                <th>Fal</th>
                <th>Ger.</th>
                <th>Pill.</th>
                <th>Lépcső</th>
                <th class="text-right">
                  Művelet
                </th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="model in models"
                :key="model.id"
                :class="{ 'row-active': Number(model.id) === Number(selectedModelId) }"
                @click="loadModelDetails(model.id)"
              >
                <td>{{ model.id }}</td>
                <td>{{ model.name || '-' }}</td>
                <td>{{ model.status || '-' }}</td>
                <td>{{ model.total_elements || 0 }}</td>
                <td>{{ model.wall_count || 0 }}</td>
                <td>{{ model.beam_count || 0 }}</td>
                <td>{{ model.column_count || 0 }}</td>
                <td>{{ model.stair_count || 0 }}</td>
                <td class="text-right">
                  <button
                    class="btn btn-sm btn-outline-danger"
                    :disabled="uploading || deletingModelId === model.id"
                    @click.stop="deleteModel(model)"
                  >
                    {{ deletingModelId === model.id ? 'Törlés...' : 'Törlés' }}
                  </button>
                </td>
              </tr>
              <tr v-if="!models.length">
                <td
                  colspan="9"
                  class="text-muted text-center"
                >
                  Nincs modell ehhez a projekthez.
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
      <div class="col-md-5">
        <div class="bim-summary">
          <h5 class="mb-1">
            Modell részletek
          </h5>
          <p v-if="modelDetails">
            #{{ modelDetails.id }} · {{ modelDetails.name }}<br>
            Sémа: {{ modelDetails.schema || '-' }}<br>
            Állapot: {{ modelDetails.status || '-' }}
          </p>
          <p
            v-else
            class="text-muted mb-0"
          >
            Válassz modellt a listából.
          </p>
        </div>
      </div>
    </div>

    <div
      v-if="modelDetails"
      class="row mb-2"
    >
      <div class="col-md-3">
        <label class="mb-50">Elem típus</label>
        <select
          v-model="filters.elementType"
          class="form-control"
        >
          <option value="">
            Összes
          </option>
          <option value="wall">
            Fal
          </option>
          <option value="beam">
            Gerenda
          </option>
          <option value="column">
            Pillér
          </option>
          <option value="stair">
            Lépcső
          </option>
        </select>
      </div>
      <div class="col-md-5">
        <label class="mb-50">Szint</label>
        <input
          v-model.trim="filters.levelName"
          type="text"
          class="form-control"
          placeholder="pl. 1. Emelet"
        >
      </div>
      <div class="col-md-4">
        <label class="mb-50">Név keresés</label>
        <input
          v-model.trim="filters.search"
          type="text"
          class="form-control"
          placeholder="IFC elem név..."
        >
      </div>
    </div>

    <div
      v-if="modelDetails"
      class="table-responsive"
    >
      <table class="table table-sm table-striped">
        <thead>
          <tr>
            <th>Típus</th>
            <th>Név</th>
            <th>Szint</th>
            <th>Méret adat</th>
            <th>Forrás</th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="element in filteredElements"
            :key="element.id"
          >
            <td>{{ elementTypeLabel(element.element_type) }}</td>
            <td>{{ element.name || '-' }}</td>
            <td>{{ element.level_name || '-' }}</td>
            <td>{{ elementMetricValue(element) }}</td>
            <td>{{ elementMetricSource(element) }}</td>
          </tr>
          <tr v-if="!filteredElements.length">
            <td
              colspan="5"
              class="text-muted text-center"
            >
              Nincs találat a szűrésre.
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </b-card-code>
</template>

<script>
import BCardCode from '@core/components/b-card-code/BCardCode.vue'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import axios from 'axios'

export default {
  name: 'BimIfcImport',
  components: {
    BCardCode,
  },
  data() {
    return {
      loading: false,
      uploading: false,
      projects: [],
      selectedProjectId: '',
      models: [],
      selectedModelId: null,
      deletingModelId: null,
      modelDetails: null,
      elements: [],
      filters: {
        elementType: '',
        levelName: '',
        search: '',
      },
    }
  },
  computed: {
    filteredElements() {
      const typeFilter = this.filters.elementType.trim().toLowerCase()
      const levelFilter = this.filters.levelName.trim().toLowerCase()
      const searchFilter = this.filters.search.trim().toLowerCase()

      return this.elements.filter(row => {
        if (!this.isRelevantRcElement(row)) return false
        if (typeFilter && String(row.element_type || '').toLowerCase() !== typeFilter) return false
        if (levelFilter && !String(row.level_name || '').toLowerCase().includes(levelFilter)) return false
        if (searchFilter && !String(row.name || '').toLowerCase().includes(searchFilter)) return false
        return true
      })
    },
  },
  async created() {
    await this.loadProjects()
  },
  methods: {
    authHeaders() {
      const token = localStorage.getItem('token') || localStorage.getItem('accessToken')
      return token ? { Authorization: `Bearer ${token}` } : {}
    },
    toastError(message) {
      this.$toast({
        component: ToastificationContent,
        props: {
          title: message || 'Hiba történt.',
          icon: 'XOctagonIcon',
          variant: 'danger',
        },
      })
    },
    toastSuccess(message) {
      this.$toast({
        component: ToastificationContent,
        props: {
          title: message || 'Sikeres művelet.',
          icon: 'CheckIcon',
          variant: 'success',
        },
      })
    },
    async loadProjects() {
      this.loading = true
      try {
        const response = await axios.get('bim-ifc/projects', { headers: this.authHeaders() })
        this.projects = response.data.projects || []
      } catch (error) {
        const message = error?.response?.data?.message || 'Nem sikerült betölteni a projekteket.'
        this.toastError(message)
      } finally {
        this.loading = false
      }
    },
    openImported3dView() {
      window.open('http://46.29.138.48/xkt/viewer.html', '_blank', 'noopener')
    },
    async onProjectChanged() {
      this.selectedModelId = null
      this.modelDetails = null
      this.elements = []
      await this.loadModels()
    },
    async loadModels() {
      if (!this.selectedProjectId) {
        this.models = []
        return
      }
      this.loading = true
      try {
        const response = await axios.get('bim-ifc/models', {
          params: { project_id: this.selectedProjectId },
          headers: this.authHeaders(),
        })
        this.models = response.data.models || []
      } catch (error) {
        const message = error?.response?.data?.message || 'Nem sikerült betölteni a modelleket.'
        this.toastError(message)
      } finally {
        this.loading = false
      }
    },
    async onIfcSelected(event) {
      const inputTarget = event?.target
      const file = inputTarget?.files?.[0]
      if (inputTarget) {
        inputTarget.value = null
      }
      if (!file) return
      if (!this.selectedProjectId) {
        this.toastError('Válassz projektet a feltöltés előtt.')
        return
      }

      const formData = new FormData()
      formData.append('project_id', this.selectedProjectId)
      formData.append('ifc_file', file)

      this.uploading = true
      try {
        await axios.post('bim-ifc/upload', formData, {
          headers: {
            ...this.authHeaders(),
            'Content-Type': 'multipart/form-data',
          },
        })
        this.toastSuccess('IFC import kész.')
        await this.loadModels()
      } catch (error) {
        const message = error?.response?.data?.message || 'IFC import hiba.'
        this.toastError(message)
      } finally {
        this.uploading = false
      }
    },
    async loadModelDetails(modelId) {
      this.selectedModelId = modelId
      this.loading = true
      try {
        const response = await axios.get(`bim-ifc/models/${modelId}`, { headers: this.authHeaders() })
        this.modelDetails = response.data.model || null
        this.elements = response.data.elements || []
      } catch (error) {
        const message = error?.response?.data?.message || 'Nem sikerült betölteni a modell részleteit.'
        this.toastError(message)
      } finally {
        this.loading = false
      }
    },
    async deleteModel(model) {
      if (!model || !model.id) return

      const modelName = model.name || `#${model.id}`
      const ok = window.confirm(`Biztosan törlöd ezt az IFC modellt?\n\n${modelName}`)
      if (!ok) return

      this.deletingModelId = model.id
      try {
        await axios.delete(`bim-ifc/models/${model.id}`, { headers: this.authHeaders() })
        this.toastSuccess('IFC modell törölve.')

        if (Number(this.selectedModelId) === Number(model.id)) {
          this.selectedModelId = null
          this.modelDetails = null
          this.elements = []
        }

        await this.loadModels()
      } catch (error) {
        const message = error?.response?.data?.message || 'Nem sikerült törölni a modellt.'
        this.toastError(message)
      } finally {
        this.deletingModelId = null
      }
    },
    elementTypeLabel(type) {
      const key = String(type || '').toLowerCase()
      if (key === 'wall') return 'Fal'
      if (key === 'beam') return 'Gerenda'
      if (key === 'column') return 'Pillér'
      if (key === 'stair') return 'Lépcső'
      return type || '-'
    },
    formatNumber(value, maxDecimals = 2) {
      const numeric = Number(value)
      if (!Number.isFinite(numeric)) return '-'
      let text = numeric.toFixed(maxDecimals)
      text = text.replace(/\.?0+$/, '')
      return text.replace('.', ',')
    },
    formatSmartLength(value, source = null) {
      if (value === null || value === undefined || value === '') return '-'
      const number = Number(value)
      if (Number.isNaN(number)) return '-'
      if (number <= 0 && !source) return '-'

      if (Math.abs(number) < 1) {
        return `${this.formatNumber(number * 100, 1)} cm`
      }
      return `${this.formatNumber(number, 2)} m`
    },
    formatFromMillimeter(mmValue) {
      const numeric = Number(String(mmValue).replace(',', '.'))
      if (!Number.isFinite(numeric)) return '-'
      return this.formatSmartLength(numeric / 1000, 'from-mm')
    },
    extractColumnDimensions(name) {
      const text = String(name || '')
      const match = text.match(/(\d+(?:[.,]\d+)?)\s*[xX]\s*(\d+(?:[.,]\d+)?)\s*mm/i)
      if (!match) return null
      return `${this.formatFromMillimeter(match[1])} x ${this.formatFromMillimeter(match[2])}`
    },
    extractRectangularDimensions(name) {
      const text = String(name || '')
      // examples:
      // "betongerenda... rectangular:200x150 mm:7690978"
      // "Rectangular:250 x 400 mm"
      const match = text.match(/rectangular\s*:\s*(\d+(?:[.,]\d+)?)\s*[xX]\s*(\d+(?:[.,]\d+)?)\s*mm/i)
        || text.match(/(\d+(?:[.,]\d+)?)\s*[xX]\s*(\d+(?:[.,]\d+)?)\s*mm/i)
      if (!match) return null
      return `${this.formatFromMillimeter(match[1])} x ${this.formatFromMillimeter(match[2])}`
    },
    extractBeamProfile(name) {
      const text = String(name || '')
      // examples:
      // "SHS (EN 10219-2):SHS80x3:8560811"
      // "Equal L:L130x12:8383371"
      const known = text.match(/\b(SHS\d+(?:[xX]\d+(?:[.,]\d+)?)*)\b/i)
      if (known && known[1]) return known[1].toUpperCase().replace(',', '.')

      const lProfile = text.match(/\bL\d+(?:[xX]\d+(?:[.,]\d+)?)*/i)
      if (lProfile && lProfile[0]) return lProfile[0].toUpperCase().replace(',', '.')

      const generic = text.match(/\b([A-Z]{1,8}\d+(?:[xX]\d+(?:[.,]\d+)?)*)\b/i)
      if (generic && generic[1]) return generic[1].toUpperCase().replace(',', '.')

      return null
    },
    isSteelLikeName(name) {
      const text = String(name || '').toUpperCase()
      if (!text) return false

      if (/\b(SHS|RHS|CHS|IPE|IPN|HEA|HEB|HEM|UPN|UPE|L\d+)\b/.test(text)) return true
      if (/\bEN\s*10219\b/.test(text)) return true
      if (/\bEQUAL\s+L\b/.test(text)) return true

      return false
    },
    isConcreteLikeName(name) {
      const text = String(name || '').toLowerCase()
      if (!text) return false

      return (
        text.includes('beton')
        || text.includes('vasbeton')
        || text.includes('vasalt')
        || text.includes('betonoszlop')
        || text.includes('betongerenda')
        || text.includes('monolit')
      )
    },
    isRelevantRcElement(element) {
      const type = String(element?.element_type || '').toLowerCase()
      const name = String(element?.name || '')

      if (type === 'beam') {
        if (this.isSteelLikeName(name)) return false
        if (this.isConcreteLikeName(name)) return true
        return false
      }

      if (type === 'column') {
        if (this.isSteelLikeName(name)) return false
        if (this.isConcreteLikeName(name)) return true
        // Ha nincs egyértelmű acél jel, oszlopot megtartjuk.
        return true
      }

      // Fal és lépcső marad, mert ezek nálatok vasbeton fókuszúak.
      return type === 'wall' || type === 'stair'
    },
    elementMetricValue(element) {
      const type = String(element?.element_type || '').toLowerCase()

      if (type === 'stair') {
        const l = Number(element?.length_m)
        const w = Number(element?.width_m)
        const h = Number(element?.height_m)
        const hasAny = (l > 0) || (w > 0) || (h > 0)
        if (!hasAny) return '-'
        const fmt = v => (v > 0 ? this.formatSmartLength(v) : '-')
        const parts = [`H: ${fmt(l)}`, `Sz: ${fmt(w)}`, `M: ${fmt(h)}`]
        const vol = Number(element?.volume_m3)
        if (vol > 0) parts.push(`V: ${this.formatNumber(vol, 3)} m³`)
        return parts.join(' · ')
      }

      if (type === 'column') {
        const dims = this.extractColumnDimensions(element?.name)
        const height = this.formatSmartLength(element?.height_m, element?.height_source)
        const heightText = height === '-' ? 'mag: -' : `mag: ${height}`
        if (dims) return `${dims} | ${heightText}`
        return heightText
      }
      if (type === 'beam') {
        const rectangular = this.extractRectangularDimensions(element?.name)
        const length = this.formatSmartLength(element?.length_m, element?.length_source)
        const lengthText = length === '-' ? 'hossz: -' : `hossz: ${length}`
        if (rectangular) return `${rectangular} | ${lengthText}`
        const profile = this.extractBeamProfile(element?.name)
        if (profile) return `${profile} | ${lengthText}`
        return lengthText
      }
      return this.formatSmartLength(element?.length_m, element?.length_source)
    },
    elementMetricSource(element) {
      const type = String(element?.element_type || '').toLowerCase()

      if (type === 'stair') {
        const sources = []
        if (element?.length_source) sources.push(`H:${element.length_source}`)
        if (element?.width_source)  sources.push(`Sz:${element.width_source}`)
        if (element?.height_source) sources.push(`M:${element.height_source}`)
        return sources.length ? sources.join(' / ') : '-'
      }

      if (type === 'column') {
        const sources = []
        if (this.extractColumnDimensions(element?.name)) sources.push('name-pattern')
        if (element?.height_source) sources.push(element.height_source)
        return sources.length ? sources.join(' + ') : '-'
      }
      if (type === 'beam') {
        const sources = []
        if (this.extractRectangularDimensions(element?.name)) {
          sources.push('name-rect')
        } else if (this.extractBeamProfile(element?.name)) {
          sources.push('name-profile')
        }
        if (element?.length_source) sources.push(element.length_source)
        return sources.length ? sources.join(' + ') : '-'
      }
      return element?.length_source || '-'
    },
  },
}
</script>

<style scoped>
.bim-header {
  display: flex;
  justify-content: space-between;
  gap: 12px;
  align-items: end;
  flex-wrap: wrap;
}

.gap-1 {
  gap: 10px;
}

.min-w-260 {
  min-width: 260px;
}

.row-active {
  background: rgba(79, 136, 235, 0.18);
}

.bim-summary {
  border: 1px solid #2f395c;
  border-radius: 10px;
  padding: 12px;
  background: rgba(26, 35, 62, 0.4);
}
</style>
